import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import axios from 'axios';

const { FiX, FiSend, FiAlertCircle, FiCheck, FiLoader } = FiIcons;

const InstantlyModal = ({ leads, onClose }) => {
  const [campaignId, setCampaignId] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  // Filter out leads without required data
  // Note: URL is not required since we can send leads with just email and company info
  const validLeads = leads.filter(lead => 
    lead.personalizedOpener && 
    !lead.personalizedOpener.startsWith('[') && 
    lead.email
  );
  
  const invalidLeads = leads.filter(lead => 
    !lead.personalizedOpener || 
    lead.personalizedOpener.startsWith('[') || 
    !lead.email
  );

  const handleSendToInstantly = async () => {
    if (!campaignId) {
      setError('Please enter a campaign ID');
      return;
    }

    if (validLeads.length === 0) {
      setError('No valid leads to send');
      return;
    }

    setSending(true);
    setError(null);
    
    try {
      const sentLeads = [];
      const failedLeads = [];
      
      // Process leads one by one
      for (const lead of validLeads) {
        try {
          // Debug the lead data being sent
          console.log('Sending lead to Instantly:', {
            email: lead.email,
            first_name: lead.firstName || '',
            last_name: lead.lastName || '',
            company_name: lead.company || '',
            website: lead.url,
            phone: lead.phone || '',
            campaign: campaignId
          });
          
          // Use the direct API endpoint instead of Supabase
          const response = await axios.post('https://wa.pressupwards.com/create-instantly-lead', {
            email: lead.email,
            first_name: lead.firstName || '',
            last_name: lead.lastName || '',
            company_name: lead.company || '',
            website: lead.url,
            phone: lead.phone || '',
            company_domain: lead.url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0],
            campaign: campaignId,
            personalization: lead.personalizedOpener
          });
          
          if (response.data && response.data.id) {
            sentLeads.push({
              ...lead,
              instantlyId: response.data.id
            });
          } else {
            failedLeads.push({
              ...lead,
              error: response.data?.message || 'Failed to add lead'
            });
          }
        } catch (err) {
          failedLeads.push({
            ...lead,
            error: err.response?.data?.message || err.message || 'Unknown error'
          });
        }
        
        // Small delay to avoid rate limits
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      setResults({
        sent: sentLeads,
        failed: failedLeads
      });
    } catch (err) {
      setError(err.message || 'Failed to send leads to Instantly');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div 
        className="modal-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Send to Instantly.ai</h2>
          <button className="close-button" onClick={onClose}>
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <div className="modal-body">
          {!results ? (
            <>
              <div className="summary-section">
                <div className="summary-item">
                  <span className="summary-label">Total Leads:</span>
                  <span className="summary-value">{leads.length}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Valid Leads:</span>
                  <span className="summary-value">{validLeads.length}</span>
                </div>
                {invalidLeads.length > 0 && (
                  <div className="summary-item error">
                    <span className="summary-label">Invalid Leads:</span>
                    <span className="summary-value">{invalidLeads.length}</span>
                  </div>
                )}
              </div>

              {invalidLeads.length > 0 && (
                <div className="warning-message">
                  <SafeIcon icon={FiAlertCircle} className="warning-icon" />
                  <div>
                    <p><strong>Some leads cannot be sent:</strong></p>
                    <ul>
                      {leads.filter(lead => !lead.email).length > 0 && (
                        <li>{leads.filter(lead => !lead.email).length} leads missing email addresses</li>
                      )}
                      {leads.filter(lead => !lead.personalizedOpener || lead.personalizedOpener.startsWith('[') ).length > 0 && (
                        <li>{leads.filter(lead => !lead.personalizedOpener || lead.personalizedOpener.startsWith('[') ).length} leads missing valid openers</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="campaign-id">Instantly Campaign ID</label>
                <input
                  id="campaign-id"
                  type="text"
                  value={campaignId}
                  onChange={(e) => setCampaignId(e.target.value)}
                  placeholder="Enter campaign ID"
                  className="form-input"
                />
                <div className="input-help">
                  Enter the ID of the campaign where you want to add these leads
                </div>
              </div>

              {error && (
                <div className="error-message">
                  <SafeIcon icon={FiAlertCircle} className="error-icon" />
                  <p>{error}</p>
                </div>
              )}

              <div className="preview-section">
                <h3>Lead Preview</h3>
                <div className="preview-list">
                  {validLeads.slice(0, 3).map((lead, index) => (
                    <div key={lead.email || `preview-${index}`} className="preview-item">
                      <div className="preview-header">
                        <div>
                          <div className="preview-title">
                            {lead.firstName && lead.lastName
                              ? `${lead.firstName} ${lead.lastName}`
                              : lead.email}
                            {lead.company && ` - ${lead.company}`}
                          </div>
                          <div className="preview-subtitle">{lead.email}</div>
                        </div>
                      </div>
                      <div className="preview-content">
                        <div className="preview-label">Personalization:</div>
                        <div className="preview-text">{lead.personalizedOpener}</div>
                      </div>
                    </div>
                  ))}
                  {validLeads.length > 3 && (
                    <div className="more-leads">
                      + {validLeads.length - 3} more leads
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="results-section">
              <div className="results-summary">
                <div className="results-item success">
                  <SafeIcon icon={FiCheck} className="results-icon" />
                  <div>
                    <div className="results-count">{results.sent.length}</div>
                    <div className="results-label">Leads Added Successfully</div>
                  </div>
                </div>
                {results.failed.length > 0 && (
                  <div className="results-item failed">
                    <SafeIcon icon={FiAlertCircle} className="results-icon" />
                    <div>
                      <div className="results-count">{results.failed.length}</div>
                      <div className="results-label">Leads Failed</div>
                    </div>
                  </div>
                )}
              </div>

              {results.failed.length > 0 && (
                <div className="failed-leads">
                  <h3>Failed Leads</h3>
                  <div className="failed-list">
                    {results.failed.map((lead, index) => (
                      <div key={lead.email || `failed-${index}`} className="failed-item">
                        <div className="failed-info">
                          <strong>{lead.email}</strong>
                          {lead.company && ` - ${lead.company}`}
                        </div>
                        <div className="failed-reason">{lead.error}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!results ? (
            <>
              <button className="cancel-button" onClick={onClose} disabled={sending}>
                Cancel
              </button>
              <button 
                className="send-button" 
                onClick={handleSendToInstantly}
                disabled={sending || validLeads.length === 0}
              >
                {sending ? (
                  <>
                    <SafeIcon icon={FiLoader} className="spinning" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} />
                    <span>Send to Instantly</span>
                  </>
                )}
              </button>
            </>
          ) : (
            <button className="done-button" onClick={onClose}>
              Done
            </button>
          )}
        </div>
      </motion.div>

      <style jsx>{`
        .modal-overlay {position: fixed;top: 0;left: 0;right: 0;bottom: 0;background-color: rgba(0,0,0,0.5);display: flex;align-items: center;justify-content: center;z-index: 1000;}
        .modal-content {background-color: white;border-radius: 8px;width: 90%;max-width: 600px;max-height: 90vh;overflow-y: auto;box-shadow: var(--shadow-lg);}
        .modal-header {display: flex;justify-content: space-between;align-items: center;padding: 1.25rem;border-bottom: 1px solid #e5e7eb;}
        .modal-header h2 {font-size: 1.25rem;font-weight: 600;margin: 0;}
        .close-button {background: none;border: none;cursor: pointer;color: var(--text-light);width: 32px;height: 32px;display: flex;align-items: center;justify-content: center;border-radius: 4px;}
        .close-button:hover {background-color: #f3f4f6;}
        .modal-body {padding: 1.25rem;}
        .summary-section {display: flex;gap: 1.25rem;margin-bottom: 1.25rem;}
        .summary-item {background-color: #f9fafb;padding: 1rem;border-radius: 8px;flex: 1;}
        .summary-item.error {background-color: #fef2f2;color: #b91c1c;}
        .summary-label {display: block;font-size: 0.875rem;color: var(--text-light);margin-bottom: 0.25rem;}
        .summary-value {font-size: 1.5rem;font-weight: 600;}
        .warning-message {display: flex;gap: 0.75rem;background-color: #fff7ed;padding: 1rem;border-radius: 8px;margin-bottom: 1.25rem;border-left: 3px solid #fb923c;align-items: flex-start;}
        .warning-icon {color: #ea580c;font-size: 1.25rem;flex-shrink: 0;}
        .warning-message p {margin: 0 0 0.5rem 0;font-size: 0.875rem;}
        .warning-message ul {margin: 0;padding-left: 1.25rem;font-size: 0.875rem;}
        .warning-message li {margin-bottom: 0.25rem;}
        .form-group {margin-bottom: 1.25rem;}
        .form-group label {display: block;font-size: 0.875rem;font-weight: 500;margin-bottom: 0.5rem;}
        .form-input {width: 100%;padding: 0.625rem;border: 1px solid #e5e7eb;border-radius: 8px;font-size: 0.875rem;}
        .form-input:focus {outline: none;border-color: var(--primary-color);box-shadow: 0 0 0 2px rgba(99,102,241,0.2);}
        .input-help {font-size: 0.75rem;color: var(--text-light);margin-top: 0.25rem;}
        .error-message {display: flex;gap: 0.75rem;background-color: #fee2e2;padding: 1rem;border-radius: 8px;margin-bottom: 1.25rem;border-left: 3px solid #ef4444;align-items: flex-start;}
        .error-icon {color: #b91c1c;font-size: 1.25rem;flex-shrink: 0;}
        .error-message p {margin: 0;font-size: 0.875rem;}
        .preview-section {margin-top: 1.5rem;}
        .preview-section h3 {font-size: 1rem;font-weight: 600;margin-bottom: 0.75rem;}
        .preview-list {display: flex;flex-direction: column;gap: 0.75rem;}
        .preview-item {background-color: #f9fafb;border-radius: 8px;padding: 1rem;}
        .preview-header {display: flex;justify-content: space-between;margin-bottom: 0.75rem;}
        .preview-title {font-weight: 500;font-size: 0.875rem;}
        .preview-subtitle {font-size: 0.75rem;color: var(--text-light);}
        .preview-content {font-size: 0.875rem;}
        .preview-label {font-weight: 500;margin-bottom: 0.25rem;}
        .preview-text {color: var(--text-color);line-height: 1.5;font-style: italic;background-color: #f3f4f6;padding: 0.75rem;border-radius: 6px;border-left: 3px solid var(--primary-light);}
        .more-leads {text-align: center;padding: 0.75rem;background-color: #f9fafb;border-radius: 8px;font-size: 0.875rem;color: var(--text-light);}
        .modal-footer {display: flex;justify-content: flex-end;gap: 0.75rem;padding: 1.25rem;border-top: 1px solid #e5e7eb;}
        .cancel-button {padding: 0.625rem 1.25rem;background-color: white;border: 1px solid #e5e7eb;border-radius: 8px;font-size: 0.875rem;font-weight: 500;cursor: pointer;}
        .send-button {display: flex;align-items: center;gap: 0.5rem;padding: 0.625rem 1.25rem;background-color: #0369a1;color: white;border: none;border-radius: 8px;font-size: 0.875rem;font-weight: 500;cursor: pointer;}
        .send-button:hover:not(:disabled) {background-color: #075985;}
        .send-button:disabled {background-color: #e5e7eb;color: #9ca3af;cursor: not-allowed;}
        .done-button {padding: 0.625rem 1.25rem;background-color: var(--primary-color);color: white;border: none;border-radius: 8px;font-size: 0.875rem;font-weight: 500;cursor: pointer;}
        .done-button:hover {background-color: var(--primary-dark);}
        .spinning {animation: spin 1.5s linear infinite;}
        @keyframes spin {0% {transform: rotate(0deg);} 100% {transform: rotate(360deg);}}
        .results-section {display: flex;flex-direction: column;gap: 1.5rem;}
        .results-summary {display: flex;gap: 1rem;}
        .results-item {flex: 1;display: flex;align-items: center;gap: 1rem;padding: 1rem;border-radius: 8px;}
        .results-item.success {background-color: #ecfdf5;}
        .results-item.failed {background-color: #fef2f2;}
        .results-icon {font-size: 1.5rem;}
        .results-item.success .results-icon {color: #065f46;}
        .results-item.failed .results-icon {color: #b91c1c;}
        .results-count {font-size: 1.25rem;font-weight: 600;}
        .results-label {font-size: 0.875rem;color: var(--text-light);}
        .failed-leads h3 {font-size: 1rem;font-weight: 600;margin-bottom: 0.75rem;}
        .failed-list {display: flex;flex-direction: column;gap: 0.5rem;}
        .failed-item {padding: 0.75rem;background-color: #fef2f2;border-radius: 8px;font-size: 0.875rem;}
        .failed-info {margin-bottom: 0.25rem;}
        .failed-reason {color: #b91c1c;}
      `}</style>
    </div>
  );
};

export default InstantlyModal;